import { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

function ScheduleCoverageIndicator({
  scheduledCount,
  postIntervalHours,
  loading = false,
  variant = 'full', // 'full', 'compact', 'minimal'
  showCTA = true,
  onUploadClick = null
}) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [isAnimated, setIsAnimated] = useState(false)

  // Trigger animation on mount and when status changes
  useEffect(() => {
    setIsAnimated(true)
    const timer = setTimeout(() => setIsAnimated(false), 1000)
    return () => clearTimeout(timer)
  }, [scheduledCount])

  // Calculate coverage in days with enhanced metrics
  const coverageData = useMemo(() => {
    if (!scheduledCount || !postIntervalHours) {
      return {
        days: 0,
        weeks: 0,
        hours: 0,
        percentage: 0,
        status: 'empty',
        statusText: t('scheduleCoverage.status.empty'),
        statusEmoji: '🚨',
        message: t('scheduleCoverage.messages.empty'),
        subMessage: t('scheduleCoverage.messages.emptySubMessage'),
        ctaText: t('scheduleCoverage.cta.emptyVariant1'),
        ctaUrgency: 'high',
        motivationalText: t('scheduleCoverage.messages.emptyMotivational'),
        progressColor: 'bg-gray-300',
        pulseAnimation: true
      }
    }

    const totalHours = scheduledCount * postIntervalHours
    const days = totalHours / 24
    const weeks = days / 7

    // Calculate percentage for progress bar (max at 30 days = 100%)
    const percentage = Math.min((days / 30) * 100, 100)

    // Format time display
    const formatTime = () => {
      if (days < 1) {
        const hours = Math.round(totalHours)
        return t('scheduleCoverage.timeUnits.hour', { count: hours })
      } else if (days < 7) {
        const roundedDays = Math.round(days)
        return t('scheduleCoverage.timeUnits.day', { count: roundedDays })
      } else if (weeks < 4) {
        const remainingDays = Math.round(days % 7)
        const fullWeeks = Math.floor(weeks)
        if (remainingDays > 0) {
          return t('scheduleCoverage.timeUnits.weeksAndDays', { weeks: fullWeeks, days: remainingDays })
        }
        return t('scheduleCoverage.timeUnits.week', { count: fullWeeks })
      } else {
        const roundedWeeks = Math.round(weeks)
        return t('scheduleCoverage.timeUnits.week', { count: roundedWeeks })
      }
    }

    // Determine status level with enhanced messaging
    let status, statusText, statusEmoji, message, subMessage, ctaText, ctaUrgency, motivationalText, progressColor, pulseAnimation

    if (days === 0) {
      status = 'empty'
      statusText = t('scheduleCoverage.status.empty')
      statusEmoji = '🚨'
      message = t('scheduleCoverage.messages.empty')
      subMessage = t('scheduleCoverage.messages.emptyZeroDaysSubMessage')
      ctaText = t('scheduleCoverage.cta.emptyVariant2')
      ctaUrgency = 'critical'
      motivationalText = t('scheduleCoverage.messages.emptyZeroDaysMotivational')
      progressColor = 'bg-red-500'
      pulseAnimation = true
    } else if (days < 3) {
      status = 'critical'
      statusText = t('scheduleCoverage.status.critical')
      statusEmoji = '⚠️'
      message = t('scheduleCoverage.messages.critical', { time: formatTime() })
      subMessage = t('scheduleCoverage.messages.criticalSubMessage')
      ctaText = t('scheduleCoverage.cta.critical')
      ctaUrgency = 'high'
      motivationalText = t('scheduleCoverage.messages.criticalMotivational', {
        count: scheduledCount
      })
      progressColor = 'bg-red-500'
      pulseAnimation = true
    } else if (days < 7) {
      status = 'low'
      statusText = t('scheduleCoverage.status.low')
      statusEmoji = '⏰'
      message = t('scheduleCoverage.messages.low', { time: formatTime() })
      subMessage = t('scheduleCoverage.messages.lowSubMessage')
      ctaText = t('scheduleCoverage.cta.low')
      ctaUrgency = 'medium'
      motivationalText = t('scheduleCoverage.messages.lowMotivational')
      progressColor = 'bg-yellow-500'
      pulseAnimation = false
    } else if (days < 14) {
      status = 'good'
      statusText = t('scheduleCoverage.status.good')
      statusEmoji = '✅'
      message = t('scheduleCoverage.messages.good', { time: formatTime() })
      subMessage = t('scheduleCoverage.messages.goodSubMessage')
      ctaText = t('scheduleCoverage.cta.good')
      ctaUrgency = 'low'
      motivationalText = t('scheduleCoverage.messages.goodMotivational')
      progressColor = 'bg-green-500'
      pulseAnimation = false
    } else {
      status = 'excellent'
      statusText = t('scheduleCoverage.status.excellent')
      statusEmoji = '🎯'
      message = t('scheduleCoverage.messages.excellent', { time: formatTime() })
      subMessage = t('scheduleCoverage.messages.excellentSubMessage')
      ctaText = t('scheduleCoverage.cta.excellent')
      ctaUrgency = 'none'
      motivationalText = t('scheduleCoverage.messages.excellentMotivational', { count: scheduledCount })
      progressColor = 'bg-blue-500'
      pulseAnimation = false
    }

    return {
      days: Math.round(days),
      weeks: Math.round(weeks),
      hours: totalHours,
      percentage,
      timeFormatted: formatTime(),
      status,
      statusText,
      statusEmoji,
      message,
      subMessage,
      ctaText,
      ctaUrgency,
      motivationalText,
      progressColor,
      pulseAnimation
    }
  }, [scheduledCount, postIntervalHours, t])

  // Status-specific styling
  const statusStyles = {
    empty: {
      bg: 'bg-gradient-to-br from-red-50 to-red-100',
      border: 'border-red-300',
      text: 'text-red-900',
      icon: 'text-red-600',
      badge: 'bg-red-600',
      badgeGlow: 'shadow-red-500/50',
      progressBg: 'bg-red-100',
      ctaBg: 'bg-red-600 hover:bg-red-700',
      ctaText: 'text-white',
      hoverBorder: 'hover:border-red-400'
    },
    critical: {
      bg: 'bg-gradient-to-br from-orange-50 to-red-50',
      border: 'border-orange-300',
      text: 'text-orange-900',
      icon: 'text-orange-600',
      badge: 'bg-orange-600',
      badgeGlow: 'shadow-orange-500/50',
      progressBg: 'bg-orange-100',
      ctaBg: 'bg-orange-600 hover:bg-orange-700',
      ctaText: 'text-white',
      hoverBorder: 'hover:border-orange-400'
    },
    low: {
      bg: 'bg-gradient-to-br from-yellow-50 to-amber-50',
      border: 'border-yellow-300',
      text: 'text-yellow-900',
      icon: 'text-yellow-600',
      badge: 'bg-yellow-500',
      badgeGlow: 'shadow-yellow-500/50',
      progressBg: 'bg-yellow-100',
      ctaBg: 'bg-yellow-600 hover:bg-yellow-700',
      ctaText: 'text-white',
      hoverBorder: 'hover:border-yellow-400'
    },
    good: {
      bg: 'bg-gradient-to-br from-green-50 to-emerald-50',
      border: 'border-green-300',
      text: 'text-green-900',
      icon: 'text-green-600',
      badge: 'bg-green-600',
      badgeGlow: 'shadow-green-500/50',
      progressBg: 'bg-green-100',
      ctaBg: 'bg-green-600 hover:bg-green-700',
      ctaText: 'text-white',
      hoverBorder: 'hover:border-green-400'
    },
    excellent: {
      bg: 'bg-gradient-to-br from-blue-50 to-indigo-50',
      border: 'border-blue-300',
      text: 'text-blue-900',
      icon: 'text-blue-600',
      badge: 'bg-gradient-to-r from-blue-600 to-indigo-600',
      badgeGlow: 'shadow-blue-500/50',
      progressBg: 'bg-blue-100',
      ctaBg: 'bg-blue-600 hover:bg-blue-700',
      ctaText: 'text-white',
      hoverBorder: 'hover:border-blue-400'
    },
  }

  const style = statusStyles[coverageData.status]

  const handleCTAClick = () => {
    if (onUploadClick) {
      onUploadClick()
    } else {
      navigate('/create')
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="flex space-x-4">
            <div className="h-12 w-12 bg-gray-300 rounded-full"></div>
            <div className="flex-1 space-y-3">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="h-3 bg-gray-300 rounded w-1/2"></div>
              <div className="h-2 bg-gray-200 rounded-full w-full"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Minimal variant for tight spaces
  if (variant === 'minimal') {
    return (
      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg ${style.bg} ${style.border} border`}>
        <span className="text-2xl mr-2">{coverageData.statusEmoji}</span>
        <span className={`text-sm font-medium ${style.text}`}>
          {coverageData.timeFormatted} {t('scheduleCoverage.labels.coverage')}
        </span>
        {showCTA && coverageData.status !== 'excellent' && (
          <button
            onClick={handleCTAClick}
            className="ml-2 text-xs font-medium underline hover:no-underline"
          >
            {t('scheduleCoverage.cta.minimal')}
          </button>
        )}
      </div>
    )
  }

  // Compact variant
  if (variant === 'compact') {
    return (
      <div className={`rounded-lg shadow-sm border ${style.border} ${style.bg} ${style.hoverBorder} p-4 transition-all duration-200`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{coverageData.statusEmoji}</span>
            <div>
              <p className={`text-sm font-semibold ${style.text}`}>{coverageData.message}</p>
              <p className="text-xs text-gray-600 mt-0.5">{coverageData.motivationalText}</p>
            </div>
          </div>
          {showCTA && coverageData.ctaUrgency !== 'none' && (
            <button
              onClick={handleCTAClick}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg ${style.ctaBg} ${style.ctaText} transition-colors`}
            >
              {coverageData.ctaText}
            </button>
          )}
        </div>
        {/* Progress bar */}
        <div className="mt-3">
          <div className={`h-1.5 ${style.progressBg} rounded-full overflow-hidden`}>
            <div
              className={`h-full ${coverageData.progressColor} rounded-full transition-all duration-700 ease-out ${
                coverageData.pulseAnimation ? 'animate-pulse' : ''
              }`}
              style={{ width: `${coverageData.percentage}%` }}
            />
          </div>
        </div>
      </div>
    )
  }

  // Full variant (default)
  return (
    <div
      className={`rounded-xl shadow-lg border-2 ${style.border} ${style.bg} ${style.hoverBorder} p-6 transition-all duration-300 hover:shadow-xl ${
        isAnimated ? 'scale-105' : 'scale-100'
      }`}
    >
      <div className="space-y-4">
        {/* Header with status badge */}
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            {/* Animated Icon */}
            <div className={`flex-shrink-0 ${style.icon} ${coverageData.pulseAnimation ? 'animate-bounce' : ''}`}>
              <div className="relative">
                {coverageData.status === 'empty' ? (
                  <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
                  </svg>
                ) : coverageData.status === 'critical' ? (
                  <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                ) : coverageData.status === 'low' ? (
                  <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : coverageData.status === 'good' ? (
                  <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                )}
                {/* Glow effect for critical states */}
                {coverageData.pulseAnimation && (
                  <div className={`absolute inset-0 rounded-full ${style.badge} opacity-25 blur-xl animate-ping`}></div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className={`text-lg font-bold ${style.text}`}>{coverageData.message}</h3>
              <p className="text-sm text-gray-600 mt-1">{coverageData.subMessage}</p>
            </div>
          </div>

          {/* Status Badge with emoji */}
          <div className="flex items-center space-x-2">
            <span className="text-2xl">{coverageData.statusEmoji}</span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white ${style.badge} shadow-lg ${style.badgeGlow}`}>
              {coverageData.statusText.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Progress Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-700">{t('scheduleCoverage.labels.backlogCoverage')}</span>
            <span className={`font-bold ${style.text}`}>{coverageData.timeFormatted}</span>
          </div>

          {/* Enhanced Progress Bar */}
          <div className="relative">
            <div className={`h-3 ${style.progressBg} rounded-full overflow-hidden`}>
              <div
                className={`h-full ${coverageData.progressColor} rounded-full transition-all duration-1000 ease-out relative overflow-hidden`}
                style={{ width: `${coverageData.percentage}%` }}
              >
                {/* Shimmer effect for excellent status */}
                {coverageData.status === 'excellent' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-shimmer"></div>
                )}
              </div>
            </div>
            {/* Milestone markers */}
            <div className="absolute inset-0 flex items-center justify-between px-1 pointer-events-none">
              <div className="w-0.5 h-5 bg-gray-300 opacity-50"></div>
              <div className="w-0.5 h-5 bg-gray-300 opacity-50"></div>
              <div className="w-0.5 h-5 bg-gray-300 opacity-50"></div>
              <div className="w-0.5 h-5 bg-gray-300 opacity-50"></div>
            </div>
          </div>

          {/* Scale labels */}
          <div className="flex justify-between text-xs text-gray-500">
            <span>{t('scheduleCoverage.scale.zeroDays')}</span>
            <span>{t('scheduleCoverage.scale.oneWeek')}</span>
            <span>{t('scheduleCoverage.scale.twoWeeks')}</span>
            <span>{t('scheduleCoverage.scale.thirtyPlusDays')}</span>
          </div>
        </div>

        {/* Motivational Text */}
        <div className={`text-sm font-medium ${style.text} text-center py-2 px-3 rounded-lg bg-white/50`}>
          {coverageData.motivationalText}
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4 pt-2">
          <div className="bg-white/60 rounded-lg px-3 py-2">
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="text-xs text-gray-500">{t('scheduleCoverage.labels.scheduledStories')}</p>
                <p className="text-sm font-bold text-gray-900">{scheduledCount || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/60 rounded-lg px-3 py-2">
            <div className="flex items-center space-x-2">
              <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-xs text-gray-500">{t('scheduleCoverage.labels.postInterval')}</p>
                <p className="text-sm font-bold text-gray-900">
                  {postIntervalHours < 24 ? `${postIntervalHours}h` : postIntervalHours === 24 ? t('scheduleCoverage.timeUnits.day', { count: 1 }) : t('scheduleCoverage.timeUnits.day', { count: postIntervalHours / 24 })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        {showCTA && (
          <div className="pt-2">
            <button
              onClick={handleCTAClick}
              className={`w-full px-6 py-3 text-sm font-bold rounded-lg ${style.ctaBg} ${style.ctaText} shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2 group`}
              aria-label={coverageData.ctaText}
            >
              <svg className="h-5 w-5 group-hover:rotate-12 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>{coverageData.ctaText}</span>
              {coverageData.ctaUrgency === 'critical' && (
                <span className="ml-2 inline-flex h-2 w-2 rounded-full bg-white animate-ping"></span>
              )}
            </button>

            {/* Secondary action for good/excellent status */}
            {(coverageData.status === 'good' || coverageData.status === 'excellent') && (
              <button
                onClick={() => navigate('/schedule')}
                className="w-full mt-2 px-4 py-2 text-xs font-medium text-gray-700 bg-white/60 rounded-lg hover:bg-white/80 transition-colors"
              >
                {t('scheduleCoverage.actions.viewSchedule')}
              </button>
            )}
          </div>
        )}

        {/* Tips for empty/critical states */}
        {(coverageData.status === 'empty' || coverageData.status === 'critical') && (() => {
          // Calculate recommended upload count based on post interval
          // Target: 7 days of content coverage
          const targetDays = 7
          const imagesPerDay = 24 / postIntervalHours
          const recommendedImages = Math.ceil(targetDays * imagesPerDay)

          // Provide a range (min = exact, max = +30% buffer)
          const minImages = recommendedImages
          const maxImages = Math.ceil(recommendedImages * 1.3)

          return (
            <div className="bg-white/80 rounded-lg p-3 border border-gray-200">
              <p className="text-xs text-gray-700">
                {t('scheduleCoverage.tip', {
                  minImages,
                  maxImages,
                  targetDays
                })}
              </p>
            </div>
          )
        })()}
      </div>
    </div>
  )
}

// Add shimmer animation to tailwind
if (typeof document !== 'undefined') {
  const style = document.createElement('style')
  style.textContent = `
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(200%); }
    }
    .animate-shimmer {
      animation: shimmer 2s linear infinite;
    }
  `
  document.head.appendChild(style)
}

export default ScheduleCoverageIndicator